import {
  AppModuleCode,
  EMPTY_PROBLEM_RCA,
  PermissionCode,
  PROBLEM_LIFECYCLE_LABELS,
  ProblemLifecycle,
  type ProblemRca,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useModule } from '@/features/auth/use-module';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import {
  KnownErrorBadge,
  ProblemLifecycleBadge,
} from '@/products/helpdesk/features/tickets/ticket-badges';
import { TicketSectionCard } from '@/products/helpdesk/features/tickets/ticket-section-card';
import {
  useLinkProblemIncident,
  useUnlinkProblemIncident,
  useUpdateProblem,
} from '@/products/helpdesk/features/tickets/use-tickets';
import { ApiRequestError } from '@/lib/api';

const RCA_FIELDS: { key: keyof ProblemRca; label: string; rows: number }[] = [
  { key: 'summary', label: 'Zusammenfassung der Ursache', rows: 3 },
  { key: 'trigger', label: 'Auslöser', rows: 2 },
  { key: 'contributingFactors', label: 'Mitwirkende Faktoren', rows: 2 },
  { key: 'evidence', label: 'Belege / Symptome', rows: 2 },
  { key: 'preventiveActions', label: 'Vorbeugende Maßnahmen', rows: 2 },
];

export function TicketProblemPanel({ ticket }: { ticket: TicketDetail }) {
  const copy = useVoiceCopy();
  const problem = ticket.problem;
  const me = useMe();
  const paths = useAppPaths();
  const canUpdate = hasPermission(me.data, PermissionCode.TICKETS_UPDATE) && !paths.isPortal;
  const knowledgeOn = useModule(AppModuleCode.KNOWLEDGE);
  const updateProblem = useUpdateProblem(String(ticket.number));
  const linkIncident = useLinkProblemIncident(String(ticket.number));
  const unlinkIncident = useUnlinkProblemIncident(String(ticket.number));

  const [lifecycle, setLifecycle] = useState<ProblemLifecycle>(
    problem?.lifecycle ?? ProblemLifecycle.IDENTIFIED,
  );
  const [rca, setRca] = useState<ProblemRca>(problem?.rca ?? { ...EMPTY_PROBLEM_RCA });
  const [workaround, setWorkaround] = useState(problem?.workaround ?? '');
  const [resolution, setResolution] = useState(problem?.resolution ?? '');
  const [knownError, setKnownError] = useState(problem?.knownError ?? false);
  const [suggestKbDraft, setSuggestKbDraft] = useState(true);
  const [incidentNumber, setIncidentNumber] = useState('');

  if (!problem) {
    return null;
  }

  const lifecycleOptions = [
    ...new Set([lifecycle, problem.lifecycle, ...problem.allowedLifecycles]),
  ];

  async function onSave() {
    try {
      await updateProblem.mutateAsync({
        lifecycle,
        rca,
        workaround,
        resolution,
        knownError,
        suggestKbDraft: lifecycle === ProblemLifecycle.RESOLVED ? suggestKbDraft : undefined,
      });
      toast.success('Problem gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onLink() {
    const parsed = Number(incidentNumber.replace(/^#/, '').trim());
    if (!Number.isInteger(parsed) || parsed < 1) {
      toast.error(copy('helpdesk.ticket.number.enter'));
      return;
    }
    try {
      await linkIncident.mutateAsync({ ticketNumber: parsed });
      toast.success(`Störung #${parsed} verknüpft.`);
      setIncidentNumber('');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Verknüpfung fehlgeschlagen.');
    }
  }

  async function onUnlink(incidentId: string) {
    try {
      await unlinkIncident.mutateAsync(incidentId);
      toast.success('Verknüpfung entfernt.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Lösen fehlgeschlagen.');
    }
  }

  return (
    <TicketSectionCard
      tone="steel"
      collapsible
      defaultOpen={false}
      toggleLabel="Problem"
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          Problem
          <ProblemLifecycleBadge lifecycle={problem.lifecycle} />
          {problem.knownError && <KnownErrorBadge />}
        </span>
      }
      description="Ursachenanalyse, Workaround und endgültige Lösung sind getrennte Felder. Der Problem-Status ist unabhängig vom Ticket-Status."
      contentClassName="space-y-4"
    >
      <div className="space-y-1.5">
        <Label htmlFor="problem-lifecycle">Problem-Status</Label>
        <Select
          value={lifecycle}
          onValueChange={(value) => setLifecycle(value as ProblemLifecycle)}
          disabled={!canUpdate}
        >
          <SelectTrigger id="problem-lifecycle" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {lifecycleOptions.map((code) => (
              <SelectItem key={code} value={code}>
                {PROBLEM_LIFECYCLE_LABELS[code]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {paths.isPortal ? null : canUpdate ? (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Ursachenanalyse (RCA)</h3>
          {RCA_FIELDS.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={`rca-${field.key}`}>{field.label}</Label>
              <textarea
                id={`rca-${field.key}`}
                rows={field.rows}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                value={rca[field.key]}
                onChange={(event) =>
                  setRca((current) => ({ ...current, [field.key]: event.target.value }))
                }
              />
            </div>
          ))}
        </div>
      ) : problem.rca.summary ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Ursache: </span>
          {problem.rca.summary}
        </p>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="problem-workaround">Workaround (vorübergehend)</Label>
        {canUpdate ? (
          <textarea
            id="problem-workaround"
            rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            value={workaround}
            onChange={(event) => setWorkaround(event.target.value)}
            placeholder="Was Betroffene tun können, bis die Ursache behoben ist …"
          />
        ) : (
          <p className="text-sm whitespace-pre-wrap">
            {problem.workaround?.trim() ? problem.workaround : '—'}
          </p>
        )}
      </div>

      {canUpdate && (
        <div className="space-y-1.5">
          <Label htmlFor="problem-resolution">Endgültige Lösung</Label>
          <textarea
            id="problem-resolution"
            rows={3}
            className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            value={resolution}
            onChange={(event) => setResolution(event.target.value)}
            placeholder="Was die Ursache dauerhaft behebt — getrennt vom Workaround."
          />
        </div>
      )}

      {canUpdate && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={knownError}
            onCheckedChange={(checked) => setKnownError(checked === true)}
          />
          Known Error (Ursache bekannt, Workaround dokumentiert)
        </label>
      )}

      {canUpdate && knowledgeOn && lifecycle === ProblemLifecycle.RESOLVED && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={suggestKbDraft}
            onCheckedChange={(checked) => setSuggestKbDraft(checked === true)}
          />
          KB-Artikel-Entwurf vorschlagen (nicht automatisch veröffentlichen)
        </label>
      )}

      {knowledgeOn && problem.kbProposal && !paths.isPortal && (
        <p className="text-sm">
          <span className="text-muted-foreground">Offener KB-Entwurf: </span>
          <Link to="/helpdesk/wissen/optimierung" className="text-primary hover:underline">
            {problem.kbProposal.proposedTitle}
          </Link>
        </p>
      )}

      {canUpdate && (
        <Button type="button" onClick={() => void onSave()} disabled={updateProblem.isPending}>
          {updateProblem.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Problem speichern
        </Button>
      )}

      <div className="space-y-3 border-t pt-4">
        <h3 className="text-sm font-medium">Zugrunde liegende Störungen</h3>
        {problem.incidents.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Störungen verknüpft.</p>
        ) : (
          <ul className="space-y-2">
            {problem.incidents.map((incident) => (
              <li key={incident.id} className="flex items-start justify-between gap-2 text-sm">
                <Link
                  to={`${paths.tickets}/${incident.number}`}
                  className="text-primary hover:underline"
                >
                  #{incident.number} · {incident.subject}
                </Link>
                {canUpdate && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={unlinkIncident.isPending}
                    onClick={() => void onUnlink(incident.id)}
                  >
                    Lösen
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canUpdate && (
          <div className="flex gap-2">
            <Input
              value={incidentNumber}
              onChange={(event) => setIncidentNumber(event.target.value)}
              placeholder="Störungsnummer, z. B. 128"
              inputMode="numeric"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => void onLink()}
              disabled={linkIncident.isPending}
            >
              {linkIncident.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Verknüpfen
            </Button>
          </div>
        )}
      </div>
    </TicketSectionCard>
  );
}
